import React, { useEffect, useState, useMemo, forwardRef } from 'react';
import '../header/Search.css';
import { transformExtent } from 'ol/proj.js';
import {
  TextField,
  IconButton,
  styled,
  Autocomplete as MUIAutocomplete,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import * as helpers from '../helpers/helpers';
import { debounce } from '@mui/material/utils';
import Grid from '@mui/material/Grid';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonIcon from '@mui/icons-material/Person';
import { ReactComponent as SearchButton } from '../header/images/magnify.svg';

const SearchAddress = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isCategoryChanged, setIsCategoryChanged] = useState(false);
  const [iconInitialClass, setIconInitialClass] = useState(
    'fs-search-icon-initial'
  );
  const [iconActiveClass, setIconActiveClass] = useState(
    'fs-search-icon-active-hidden'
  );
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);

  const categories = [
    { id: 1, name: 'Ime Potrošača' },
    { id: 2, name: 'Šifra Potrošača' },
  ];

  const CustomListbox = forwardRef(function CustomListbox(props, ref) {
    const { children } = props;
    return (
      <ul {...props}>
        {isCategoryDropdownOpen && (
          <Stack
            spacing={1}
            sx={{
              padding: '4px 16px 8px 8px',
              borderBottom: '1px solid #ddd',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography variant="h9">Odaberi kategoriju pretrage</Typography>
          </Stack>
        )}
        {children}
      </ul>
    );
  });

  useEffect(() => {
    const sidebarChangedListner = window.emitter.addListener(
      'sidebarChanged',
      (isSidebarOpen) => setIsSidebarOpen(isSidebarOpen)
    );

    return () => {
      window.emitter.removeListener('sidebarChanged', (isSidebarOpen) =>
        setIsSidebarOpen(isSidebarOpen)
      );
    };
  }, []);

  const geoUrl = process.env.REACT_APP_BACKEND_API;

  const fetch = useMemo(
    () =>
      debounce((sql, layerName, searchParam, callback) => {
        helpers.getWFSGeoJSON(
          `${geoUrl}/operations/`,
          layerName,
          callback,
          searchParam,
          null,
          sql,
          8
        );
      }, 400),
    []
  );

  useEffect(() => {
    let active = true;

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      setIconInitialClass('fs-search-icon-initial');
      setIconActiveClass('fs-search-icon-active-hidden');
      return undefined;
    }
    setIconInitialClass('fs-search-icon-initial-hidden');
    setIconActiveClass('fs-search-icon-active');
    setLoading(true);

    let sql = '';
    if (isCategoryChanged && inputValue !== '') {
      if (sql === '') sql += "pis_sifrapm ILIKE '%25" + inputValue + "%25'";
    } else {
      if (sql === '') sql += "pis_naziv ILIKE '%25" + inputValue + "%25'";
    }

    if (sql === '') return;

    const layerName = 'gracanica:Potrosaci';
    const searchParam = isCategoryChanged ? 'pis_sifrapm' : 'pis_naziv';

    fetch(sql, layerName, searchParam, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          setAddressResult(results);
        }
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const setAddressResult = (result) => {
    setOptions(result);
    setLoading(false);
  };

  const handleClear = () => {
    setInputValue('');
    setValue(null);
    setKey((prevKey) => prevKey + 1);
    setIsDropdownOpen(false);
  };

  const onAddressClick = (feature) => {
    if (isCategoryChanged) {
      setInputValue(feature.values_.pis_sifrapm);
      setValue(feature.values_.pis_sifrapm);
    } else {
      setInputValue(feature.values_.pis_naziv);
      setValue(feature.values_.pis_naziv);
    }

    const sourceProjection = 'EPSG:3908';
    const destProjection = 'EPSG:3857';

    const originalExtent = feature.getGeometry().getExtent();
    var extent = transformExtent(
      [
        originalExtent[0],
        originalExtent[1],
        originalExtent[2],
        originalExtent[3],
      ],
      sourceProjection,
      destProjection
    );

    window.map.getView().fit(extent, window.map.getSize(), {
      constrainResolution: false,
      duration: 500,
    });
    window.map.getView().setZoom(21);
  };

  const onCategoryClick = (category) => {
    category.id === 2
      ? setIsCategoryChanged(true)
      : setIsCategoryChanged(false);
    setIsCategoryDropdownOpen(false);
    handleClear();
  };

  const handleCategoryDropdown = (e) => {
    e.stopPropagation();
    e.preventDefault();

    setIsCategoryDropdownOpen((prev) => !prev);
    setIsDropdownOpen(false);
  };

  const handleCategoryDropdownClose = () => {
    setIsCategoryDropdownOpen(false);
  };

  const handleDropdownOpen = () => {
    if (inputValue !== '' && options) {
      setIsDropdownOpen(true);
      setIsCategoryDropdownOpen(false);
    }
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
    setIsCategoryDropdownOpen(false);
  };

  return (
    <div
      className={
        isSidebarOpen ? 'search-position-slideout' : 'search-position-slidein'
      }
    >
      <SearchWithDropdown
        classes={{ root: 'fs-search-textbox-mui' }}
        id={'fs-search-textbox-mui'}
        key={key}
        getOptionLabel={(option) =>
          !isCategoryDropdownOpen
            ? typeof option === 'string'
              ? option
              : isCategoryChanged
              ? option.values_.pis_sifrapm
              : option.values_.pis_naziv
            : typeof option === 'string'
            ? option
            : option.name
        }
        loadingText="Pretražujem..."
        options={isCategoryDropdownOpen ? categories : options}
        open={isCategoryDropdownOpen || isDropdownOpen}
        filterOptions={(x) => x}
        autoComplete
        includeInputInList
        filterSelectedOptions
        value={value}
        noOptionsText={!options && 'Nema adresa'}
        fullWidth
        loading={loading}
        freeSolo
        disableClearable
        clearOnEscape
        onChange={(event, newValue) => {
          setOptions(newValue ? [newValue, ...options] : options);
          setValue(newValue);
        }}
        onInputChange={(e, newValue) => {
          setInputValue(newValue);
          setIsDropdownOpen(newValue.length > 0);
          setIsCategoryDropdownOpen(false);
        }}
        onOpen={handleDropdownOpen}
        onClose={handleDropdownClose}
        ListboxComponent={CustomListbox}
        slotProps={{
          popper: {
            className: `popper-dropdown ${isSidebarOpen ? 'popper-slideout' : 'popper-slidein'}`,
          },
        }}
        renderInput={(params) => (
          <TextField
            classes={{ root: 'fs-search-textbox-input-mui' }}
            hiddenLabel
            placeholder={
              isCategoryChanged
                ? 'Pretraži šifru potrošača...'
                : 'Pretraži potrošača...'
            }
            {...params}
            InputProps={{
              ...params.InputProps,
              sx: {
                borderRadius:
                  isDropdownOpen || isCategoryDropdownOpen
                    ? '16px 16px 0 0'
                    : '16px',
              },
              type: 'text',
              startAdornment: (
                <IconButton
                  aria-label="clear search"
                  edge="end"
                  size="small"
                  style={{ marginRight: 0, paddingTop: '3px' }}
                  onMouseDown={handleCategoryDropdown}
                >
                  {isCategoryDropdownOpen ? (
                    <KeyboardArrowUpIcon className="fs-search-icon-initial-arrow" />
                  ) : (
                    <KeyboardArrowDownIcon className="fs-search-icon-initial-arrow" />
                  )}
                </IconButton>
              ),
              endAdornment: (
                <IconButton
                  aria-label="clear search"
                  edge="end"
                  size="small"
                  style={{ marginRight: 0, paddingTop: '3px' }}
                  onMouseDown={inputValue !== '' ? handleClear : () => {}}
                >
                  <ClearIcon
                    className={iconActiveClass}
                    fontSize="20px"
                    color="#c7c7c7"
                  />

                  <SearchButton className={iconInitialClass} />
                </IconButton>
              ),
            }}
            onFocus={(event) => event.preventDefault()}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const id = props.id;

          return isCategoryDropdownOpen ? (
            <li
              {...props}
              style={{
                backgroundColor:
                  (option.id === 1 && !isCategoryChanged) ||
                  (option.id === 2 && isCategoryChanged)
                    ? '#ddd'
                    : 'transparent',
              }}
              onClick={() => onCategoryClick(option)}
            >
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  {option.name === 'Adresa' ? (
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  ) : (
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                  )}
                </Grid>
                <Grid
                  sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
                >
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {option.name}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          ) : (
            <li
              key={id}
              {...optionProps}
              onClick={() => onAddressClick(option)}
            >
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid item sx={{ display: 'flex', width: 44 }}>
                  {isCategoryChanged ? (
                    <PersonIcon sx={{ color: 'text.secondary' }} />
                  ) : (
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  )}
                </Grid>
                <Grid
                  item
                  sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}
                >
                  <Typography
                    key={key}
                    variant="body2"
                    sx={{ color: 'text.secondary' }}
                  >
                    {isCategoryChanged
                      ? option.values_.pis_sifrapm
                      : option.values_.pis_naziv}
                  </Typography>
                </Grid>
              </Grid>
            </li>
          );
        }}
        PaperComponent={(props) => (
          <CustomPaper {...props}>
            {options.length === 0 &&
            inputValue &&
            !loading &&
            !isCategoryDropdownOpen ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: '10px', textAlign: 'center' }}
              >
                {isCategoryChanged
                  ? 'Nema potrošača sa tom šifrom!'
                  : 'Nema potrošača sa tim imenom!'}
              </Typography>
            ) : (
              props.children
            )}
          </CustomPaper>
        )}
      />
    </div>
  );
};

export default SearchAddress;

const SearchWithDropdown = styled(MUIAutocomplete)({
  '.MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
  },
});

const CustomPaper = styled(Paper)(({ theme }) => ({
  borderTop: '1px solid #d4d4d4',
  borderRadius: '0 0 16px 16px',
}));
